/**
 * NEATLY LUXURY HEADER JAVASCRIPT
 * Ultra-stylish header with premium interactive effects
 */

class NeatlyHeader {
  constructor() {
    this.header = document.querySelector('[data-header]');
    this.mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
    this.mobileMenu = document.querySelector('[data-mobile-menu]');
    this.mobileMenuIcon = document.querySelector('.mobile-menu-icon');
    this.mobileMenuClose = document.querySelector('.mobile-menu-close');
    this.cartLink = document.querySelector('.cart-link');
    this.cartCount = document.querySelector('.cart-count');
    
    this.isScrolled = false;
    this.isMobileMenuOpen = false;
    this.scrollThreshold = 50;
    
    this.init();
  }

  init() {
    try {
      this.bindEvents();
      this.initScrollDetection();
      this.initMobileMenu();
      this.initCartAnimations();
      this.initAccessibility();
    } catch (error) {
      console.log('Header init error:', error);
    }
  }

  bindEvents() {
    try {
      // Mobile menu toggle
      if (this.mobileMenuToggle && this.toggleMobileMenu) {
        this.mobileMenuToggle.addEventListener('click', () => this.toggleMobileMenu());
      }
    } catch (error) {
      console.log('Bind events error:', error);
    }
  }

  initScrollDetection() {
    // Initial state
    this.updateHeaderState();
    
    // Throttled scroll handler for performance
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateHeaderState();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  updateHeaderState() {
    const scrollY = window.scrollY;
    const wasScrolled = this.isScrolled;
    this.isScrolled = scrollY > this.scrollThreshold;
    
    if (wasScrolled !== this.isScrolled) {
      if (this.header) {
        this.header.classList.toggle('scrolled', this.isScrolled);
        
        // Add smooth transition classes
        this.header.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      }
    }
  }

  initMobileMenu() {
    // Close mobile menu on larger screens
    this.closeMobileMenuOnResize();
    
    // Add mobile menu links functionality
    const mobileNavLinks = document.querySelectorAll('.mobile-nav-link');
    mobileNavLinks.forEach(link => {
      link.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    });
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    
    if (this.mobileMenu) {
      if (this.isMobileMenuOpen) {
        this.openMobileMenu();
      } else {
        this.closeMobileMenu();
      }
    }
  }

  openMobileMenu() {
    this.isMobileMenuOpen = true;
    
    if (this.mobileMenu) {
      this.mobileMenu.classList.remove('hidden');
      this.mobileMenu.style.animation = 'slideDown 0.3s ease-out';
    }
    
    // Toggle icons
    if (this.mobileMenuIcon && this.mobileMenuClose) {
      this.mobileMenuIcon.classList.add('hidden');
      this.mobileMenuClose.classList.remove('hidden');
    }
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Set focus for accessibility
    if (this.mobileMenu) {
      const firstLink = this.mobileMenu.querySelector('a');
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 100);
      }
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    
    if (this.mobileMenu) {
      this.mobileMenu.style.animation = 'slideUp 0.3s ease-out';
      setTimeout(() => {
        this.mobileMenu.classList.add('hidden');
      }, 300);
    }
    
    // Toggle icons
    if (this.mobileMenuIcon && this.mobileMenuClose) {
      this.mobileMenuIcon.classList.remove('hidden');
      this.mobileMenuClose.classList.add('hidden');
    }
    
    // Restore body scroll
    document.body.style.overflow = '';
  }

  handleOutsideClick(event) {
    if (this.isMobileMenuOpen && this.mobileMenu && !this.mobileMenu.contains(event.target) && !this.mobileMenuToggle.contains(event.target)) {
      this.closeMobileMenu();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isMobileMenuOpen) {
      this.closeMobileMenu();
      this.mobileMenuToggle.focus();
    }
  }

  handleResize() {
    this.closeMobileMenuOnResize();
  }

  closeMobileMenuOnResize() {
    if (window.innerWidth >= 1024 && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  animateCartIcon() {
    if (this.cartCount) {
      this.cartCount.style.transform = 'scale(1.2)';
      setTimeout(() => {
        this.cartCount.style.transform = 'scale(1)';
      }, 200);
    }
  }

  // handleSearchToggle removed - now handled in main-header.liquid

  handleLogoClick() {
    // Add logo click tracking or special effects
    console.log('Logo clicked');
    
    // Optional: Add special animation on logo click
    const logoIcon = document.querySelector('.logo-icon');
    if (logoIcon) {
      logoIcon.style.transform = 'scale(1.1) rotate(360deg)';
      setTimeout(() => {
        logoIcon.style.transform = '';
      }, 600);
    }
  }

  initCartAnimations() {
    // Animate cart count when it changes
    if (this.cartCount) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            this.animateCartCountChange();
          }
        });
      });
      
      observer.observe(this.cartCount, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  animateCartCountChange() {
    if (this.cartCount) {
      this.cartCount.style.animation = 'bounce 0.6s ease-in-out';
      setTimeout(() => {
        this.cartCount.style.animation = '';
      }, 600);
    }
  }

  initAccessibility() {
    // Add ARIA labels and keyboard navigation
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
      this.mobileMenuToggle.setAttribute('aria-controls', 'mobile-menu');
    }
    
    if (this.mobileMenu) {
      this.mobileMenu.setAttribute('id', 'mobile-menu');
      this.mobileMenu.setAttribute('role', 'navigation');
      this.mobileMenu.setAttribute('aria-label', 'Mobile navigation');
    }
    
    // Update ARIA states when menu opens/closes
    this.updateAriaStates();
  }

  updateAriaStates() {
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.setAttribute('aria-expanded', this.isMobileMenuOpen.toString());
    }
  }

  // Public methods for external use
  static updateCartCount(count) {
    const cartCount = document.querySelector('.cart-count');
    if (cartCount) {
      cartCount.textContent = count;
    }
  }

  static showNotification(message, type = 'info') {
    // Create notification system for header messages
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 100px;
      right: 20px;
      background: var(--sage);
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Auto remove
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Additional CSS for slide animations
const additionalStyles = `
  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
  
  .notification {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 0.875rem;
  }
  
  .notification-success {
    background: var(--sage) !important;
  }
  
  .notification-error {
    background: var(--red-400) !important;
  }
  
  .notification-warning {
    background: var(--tan) !important;
  }
`;

// Inject additional styles
const styleSheet = document.createElement('style');
styleSheet.textContent = additionalStyles;
document.head.appendChild(styleSheet);

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.neatlyHeader = new NeatlyHeader();
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NeatlyHeader;
}

// Performance optimization: Intersection Observer for logo animations
if ('IntersectionObserver' in window) {
  const logoObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('logo-visible');
      }
    });
  }, {
    threshold: 0.1
  });
  
  document.addEventListener('DOMContentLoaded', () => {
    const logoContainer = document.querySelector('.logo-container');
    if (logoContainer) {
      logoObserver.observe(logoContainer);
    }
  });
}

// Utility functions for theme integration
window.NeatlyHeaderUtils = {
  // Update cart count from Shopify Ajax API
  updateCartCount: (count) => {
    NeatlyHeader.updateCartCount(count);
  },
  
  // Show notification messages
  showNotification: (message, type) => {
    NeatlyHeader.showNotification(message, type);
  },
  
  // Get current header state
  getHeaderState: () => {
    return {
      isScrolled: window.neatlyHeader?.isScrolled || false,
      isMobileMenuOpen: window.neatlyHeader?.isMobileMenuOpen || false
    };
  },
  
  // Force header state update
  updateHeaderState: () => {
    if (window.neatlyHeader) {
      window.neatlyHeader.updateHeaderState();
    }
  }
};